import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Users, Plus, LogOut, Calculator, MapPin, Trash2, CreditCard, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import NotificationBell, { refreshNotifications } from '../components/NotificationBell';
import Logo from '../components/Logo';
import ReferralCard from '../components/ReferralCard';
import PaymentModal from '../components/PaymentModal';
import RatingModal from '../components/RatingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerDashboard({ user }) {
  const navigate = useNavigate();
  const [errands, setErrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [errandType, setErrandType] = useState('parcel');
  const [stopsList, setStopsList] = useState([{ address: '', notes: '' }]);
  const [description, setDescription] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [paymentErrand, setPaymentErrand] = useState(null);
  const [ratingErrand, setRatingErrand] = useState(null);

  useEffect(() => { fetchErrands(); }, []);

  useEffect(() => {
    if (stopsList.length > 0) calculatePrice();
  }, [errandType, stopsList.length]);

  const fetchErrands = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/errands/my`, { credentials: 'include' });
      if (res.ok) setErrands(await res.json());
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ errand_type: errandType, stops: stopsList.length })
      });
      if (res.ok) setCalculatedPrice(await res.json());
    } catch (err) { /* silent */ }
  };

  const addStop = () => setStopsList([...stopsList, { address: '', notes: '' }]);
  const removeStop = (idx) => setStopsList(stopsList.filter((_, i) => i !== idx));
  const updateStop = (idx, field, value) => {
    const next = [...stopsList];
    next[idx][field] = value;
    setStopsList(next);
  };

  const handleCreateErrand = async (e) => {
    e.preventDefault();
    if (stopsList.some(s => !s.address.trim())) {
      toast.error('Please fill in all stop addresses');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/errands`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          errand_type: errandType,
          stops_data: stopsList.map(s => ({ address: s.address.trim(), notes: s.notes.trim() || null })),
          description
        })
      });
      if (!res.ok) throw new Error('Failed to create errand');
      const created = await res.json();
      toast.success('Errand created! Complete payment to publish.');
      setShowCreateDialog(false);
      setStopsList([{ address: '', notes: '' }]);
      setDescription('');
      setErrandType('parcel');
      await fetchErrands();
      // Automatically open payment modal
      setPaymentErrand(created);
      refreshNotifications();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  };

  const getStatusColor = (status) => {
    const map = {
      pending: 'bg-orange-100 text-orange-700',
      accepted: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      completed: 'bg-teal-100 text-teal-700',
      cancelled: 'bg-slate-100 text-slate-700'
    };
    return map[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Logo size="md" showText />
            <div className="flex items-center gap-3">
              <NotificationBell />
              <span data-testid="user-name" className="text-slate-700 font-medium hidden md:block">{user?.name}</span>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full px-4 py-2 font-medium btn-hover flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 lg:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">My Errands</h2>
            <p className="text-slate-600 mb-4">Manage your errand requests</p>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button
                  data-testid="create-errand-btn"
                  className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full px-6 py-3 font-bold shadow-md btn-hover flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create Errand
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="create-errand-dialog" className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-teal-700">Create New Errand</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateErrand} className="space-y-5 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Errand Type</label>
                  <Select value={errandType} onValueChange={setErrandType}>
                    <SelectTrigger data-testid="errand-type-select" className="bg-slate-50 border-slate-200 rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="parcel">Parcel Delivery</SelectItem>
                      <SelectItem value="queue">Queue Assistance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-700">Stops ({stopsList.length})</label>
                    <Button
                      data-testid="add-stop-btn"
                      type="button"
                      onClick={addStop}
                      className="bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-full px-3 py-1 text-xs font-medium btn-hover flex items-center gap-1"
                    >
                      <Plus className="w-3 h-3" /> Add stop
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {stopsList.map((stop, idx) => (
                      <div key={idx} data-testid={`stop-row-${idx}`} className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-teal-700 flex-shrink-0" />
                          <span className="text-xs font-medium text-slate-600">Stop {idx + 1}</span>
                          {stopsList.length > 1 && (
                            <button
                              data-testid={`remove-stop-${idx}`}
                              type="button"
                              onClick={() => removeStop(idx)}
                              className="ml-auto text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <Input
                          data-testid={`stop-address-${idx}`}
                          value={stop.address}
                          onChange={(e) => updateStop(idx, 'address', e.target.value)}
                          placeholder="Address or location"
                          className="bg-white border-slate-200 focus:ring-2 focus:ring-teal-700/20 focus:border-teal-700 rounded-lg text-sm"
                          required
                        />
                        <Input
                          data-testid={`stop-notes-${idx}`}
                          value={stop.notes}
                          onChange={(e) => updateStop(idx, 'notes', e.target.value)}
                          placeholder="Optional notes (gate code, floor, etc.)"
                          className="bg-white border-slate-200 focus:ring-2 focus:ring-teal-700/20 focus:border-teal-700 rounded-lg text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                  <Textarea
                    data-testid="description-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="bg-slate-50 border-slate-200 focus:ring-2 focus:ring-teal-700/20 focus:border-teal-700 rounded-lg"
                    placeholder="What needs to be done?"
                    rows={3}
                    required
                  />
                </div>

                {calculatedPrice && (
                  <Card data-testid="price-preview" className="bg-teal-50 border-2 border-orange-500 rounded-xl p-4 pricing-glow">
                    <div className="flex items-center gap-2 mb-2">
                      <Calculator className="w-5 h-5 text-teal-700" />
                      <h3 className="font-bold text-teal-700">Estimated Price</h3>
                    </div>
                    <div className="space-y-1 text-sm text-slate-700">
                      <p>Base: R{calculatedPrice.base_price.toFixed(2)}</p>
                      <p>{calculatedPrice.stops} stops × R{calculatedPrice.per_stop_charge.toFixed(2)} = R{(calculatedPrice.stops * calculatedPrice.per_stop_charge).toFixed(2)}</p>
                      <p className="text-lg font-bold text-teal-700 pt-2 border-t border-teal-200">Total: R{calculatedPrice.total_price.toFixed(2)}</p>
                    </div>
                  </Card>
                )}

                <Button
                  data-testid="submit-errand-btn"
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-full py-3 font-medium shadow-lg btn-hover"
                >
                  {submitting ? 'Creating...' : 'Continue to Payment'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
          <div className="lg:col-span-1">
            <ReferralCard />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading errands...</p>
          </div>
        ) : errands.length === 0 ? (
          <Card data-testid="no-errands-message" className="bg-white border border-slate-100 shadow-sm rounded-xl p-12 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2">No errands yet</h3>
            <p className="text-slate-600">Create your first errand to get started</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {errands.map((errand) => (
              <Card key={errand.errand_id} data-testid={`errand-card-${errand.errand_id}`} className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 card-hover">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {errand.errand_type === 'parcel' ? (
                      <div className="bg-teal-100 w-10 h-10 rounded-full flex items-center justify-center">
                        <Package className="w-5 h-5 text-teal-700" />
                      </div>
                    ) : (
                      <div className="bg-orange-100 w-10 h-10 rounded-full flex items-center justify-center">
                        <Users className="w-5 h-5 text-orange-500" />
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-slate-900">{errand.errand_type === 'parcel' ? 'Parcel Delivery' : 'Queue Assistance'}</p>
                      <p className="text-sm text-slate-600">{errand.stops} {errand.stops === 1 ? 'stop' : 'stops'}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(errand.status)}`}>
                    {errand.status.replace('_', ' ')}
                  </span>
                </div>

                {errand.stops_data && errand.stops_data.length > 0 && (
                  <div className="mb-3 space-y-1">
                    {errand.stops_data.slice(0, 2).map((s, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                        <MapPin className="w-3 h-3 text-teal-700 flex-shrink-0" />
                        <span className="truncate">{s.address}</span>
                      </div>
                    ))}
                    {errand.stops_data.length > 2 && (
                      <p className="text-xs text-slate-500 pl-5">+{errand.stops_data.length - 2} more</p>
                    )}
                  </div>
                )}

                <p className="text-slate-700 text-sm mb-3 line-clamp-2">{errand.description}</p>

                {errand.runner_name && (
                  <p className="text-sm text-slate-600 mb-2">Runner: <span className="font-medium">{errand.runner_name}</span></p>
                )}

                <div className="flex items-center justify-between mt-3">
                  <p className="text-lg font-bold text-teal-700">R{errand.price.toFixed(2)}</p>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    errand.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {errand.payment_status === 'paid' ? '✓ Paid' : 'Unpaid'}
                  </span>
                </div>

                {errand.payment_status === 'unpaid' && (
                  <Button
                    data-testid={`pay-btn-${errand.errand_id}`}
                    onClick={() => setPaymentErrand(errand)}
                    className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-white rounded-full py-2 font-medium btn-hover flex items-center justify-center gap-2"
                  >
                    <CreditCard className="w-4 h-4" /> Pay Now
                  </Button>
                )}

                {errand.status === 'completed' && !errand.rated && (
                  <Button
                    data-testid={`rate-btn-${errand.errand_id}`}
                    onClick={() => setRatingErrand(errand)}
                    className="w-full mt-4 bg-teal-700 hover:bg-teal-800 text-white rounded-full py-2 font-medium btn-hover flex items-center justify-center gap-2"
                  >
                    <Star className="w-4 h-4" /> Rate Runner
                  </Button>
                )}

                {errand.status === 'completed' && errand.rated && (
                  <div className="mt-4 text-center py-2 bg-teal-50 rounded-full text-teal-700 text-sm font-medium">
                    ✓ Rating submitted
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </main>

      <PaymentModal
        open={!!paymentErrand}
        onOpenChange={(o) => !o && setPaymentErrand(null)}
        errand={paymentErrand}
        onSuccess={fetchErrands}
      />
      <RatingModal
        open={!!ratingErrand}
        onOpenChange={(o) => !o && setRatingErrand(null)}
        errand={ratingErrand}
        onSuccess={fetchErrands}
      />
    </div>
  );
}
